import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, CheckCircle2, Clock, Edit, Eye, MessageSquare, Phone, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { getFollowUps, updateLeadFollowUp } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { StatusBadge } from '../ui/ActionModals';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Textarea } from '../ui/textarea';

const priorityStyles = {
  High: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  Medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  Low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const followUpTypes = ['Call', 'Meeting', 'Site Visit', 'WhatsApp', 'Email'];
const filterStatusOptions = ['All', 'Scheduled', 'Overdue', 'Completed'];
const filterPriorityOptions = ['All', 'High', 'Medium', 'Low'];
const bucketTabs = [
  { key: 'today', label: "Today's" },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'completed', label: 'Completed' },
];

function fmtDateTime(value) {
  if (!value) return { date: '-', time: '-' };
  const dt = new Date(value);
  return {
    date: dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
  };
}

function toDateInputValue(value) {
  if (!value) return '';
  const dt = new Date(value);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

function toTimeInputValue(value) {
  if (!value) return '';
  const dt = new Date(value);
  return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}

function buildPaginationItems(page, totalPages) {
  const pages = [];
  for (let current = 1; current <= totalPages; current += 1) {
    if (current === 1 || current === totalPages || Math.abs(current - page) <= 1) {
      pages.push(current);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return pages;
}

function FollowUpPagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const items = buildPaginationItems(page, totalPages);
  return (
    <Pagination className="justify-end">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page > 1) onPageChange(page - 1);
            }}
            className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
        {items.map((item, index) => (
          <PaginationItem key={`${item}-${index}`}>
            {item === '...' ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                href="#"
                isActive={item === page}
                onClick={(event) => {
                  event.preventDefault();
                  onPageChange(item);
                }}
              >
                {item}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page < totalPages) onPageChange(page + 1);
            }}
            className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

export function FollowUpEditDialog({ item, open, onClose, onSaved }) {
  const { token } = useAuth();
  const [form, setForm] = useState({
    type: 'Call',
    date: '',
    time: '',
    priority: 'Medium',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!item) return;
    setForm({
      type: item.follow_up_type || item.followUpType || 'Call',
      date: toDateInputValue(item.follow_up_at || item.followUpAt),
      time: toTimeInputValue(item.follow_up_at || item.followUpAt),
      priority: item.priority || 'Medium',
      notes: item.follow_up_note || item.followUpNote || '',
    });
  }, [item]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!token || !item?.follow_up_id) return;
    setSaving(true);
    try {
      await updateLeadFollowUp(token, item.follow_up_id, {
        follow_up_type: form.type,
        date: form.date,
        time: form.time,
        priority: form.priority,
        notes: form.notes || null,
        status: 'Scheduled',
      });
      toast.success('Follow-up updated');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to update follow-up');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Edit Follow-Up</DialogTitle>
          <DialogDescription>{item?.lead_name || item?.leadName || ''}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Follow-Up Type</Label>
            <Select value={form.type} onValueChange={(value) => setForm((prev) => ({ ...prev, type: value }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {followUpTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))} required />
            </div>
            <div className="space-y-1.5">
              <Label>Time</Label>
              <Input type="time" value={form.time} onChange={(e) => setForm((prev) => ({ ...prev, time: e.target.value }))} required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(value) => setForm((prev) => ({ ...prev, priority: value }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Note</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function FollowUpCompleteDialog({ item, open, onClose, onSaved }) {
  const { token } = useAuth();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNote(item?.follow_up_note || item?.followUpNote || '');
  }, [item]);

  const handleComplete = async (e) => {
    e.preventDefault();
    if (!token || !item?.follow_up_id) return;
    setSaving(true);
    try {
      await updateLeadFollowUp(token, item.follow_up_id, {
        status: 'Completed',
        notes: note || null,
      });
      toast.success('Follow-up marked completed');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to complete follow-up');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Mark Follow-Up Complete</DialogTitle>
          <DialogDescription>{item?.lead_name || item?.leadName || ''}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleComplete} className="space-y-4">
          <div className="rounded-xl border bg-muted/40 p-3 text-sm">
            <p className="font-medium">{fmtDateTime(item?.follow_up_at || item?.followUpAt).date}</p>
            <p className="text-muted-foreground">{fmtDateTime(item?.follow_up_at || item?.followUpAt).time}</p>
          </div>
          <div className="space-y-1.5">
            <Label>Completion Note</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={saving}>
              {saving ? 'Saving...' : 'Mark Complete'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FollowUpTable({ rows, onViewLead, onEdit, onComplete, showCompletedAt = false, readOnly = false }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Lead</TableHead>
          <TableHead>Contact</TableHead>
          <TableHead>Follow-Up</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Status</TableHead>
          {showCompletedAt ? <TableHead>Completed At</TableHead> : null}
          <TableHead>Priority</TableHead>
          <TableHead>Owner</TableHead>
          <TableHead>Note</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((item) => {
          const schedule = fmtDateTime(item.follow_up_at || item.followUpAt);
          const completed = fmtDateTime(item.completed_at || item.completedAt);
          return (
            <TableRow key={item.follow_up_id} className="hover:bg-muted/30 transition-colors">
              <TableCell className="font-medium">
                <div className="flex flex-col">
                  <span>{item.lead_name || item.leadName}</span>
                  <span className="text-xs text-muted-foreground font-mono">{item.lead_id || item.leadId}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{item.phone || '-'}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium">{schedule.date}</span>
                  <span className="text-xs text-muted-foreground">{schedule.time}</span>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline">{item.follow_up_type || item.followUpType}</Badge>
              </TableCell>
              <TableCell>
                <StatusBadge status={item.status} />
              </TableCell>
              {showCompletedAt ? (
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{completed.date}</span>
                    <span className="text-xs text-muted-foreground">{completed.time}</span>
                  </div>
                </TableCell>
              ) : null}
              <TableCell>
                <Badge variant="outline" className={priorityStyles[item.priority] || ''}>{item.priority}</Badge>
              </TableCell>
              <TableCell>{item.assigned_to_name || item.assignedToName || '-'}</TableCell>
              <TableCell className="max-w-[220px] truncate text-sm text-muted-foreground">
                {item.follow_up_note || item.followUpNote || '-'}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => onViewLead(item)}>
                    <Eye className="mr-1 h-3.5 w-3.5" />
                    View
                  </Button>
                  {!readOnly ? (
                    <Button size="sm" variant="outline" onClick={() => onEdit(item)}>
                      <Edit className="mr-1 h-3.5 w-3.5" />
                      Edit
                    </Button>
                  ) : null}
                  {!readOnly && item.status !== 'Completed' ? (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onComplete(item)}>
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                      Complete
                    </Button>
                  ) : null}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={showCompletedAt ? 10 : 9} className="py-12 text-center text-muted-foreground">
              No follow-up records found.
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    </Table>
  );
}

export function FollowUpsBucketPage({ roleBasePath }) {
  const { token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [completingItem, setCompletingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
  });

  const bucket = useMemo(() => {
    if (location.pathname.includes('/completed')) return 'completed';
    if (location.pathname.includes('/upcoming')) return 'upcoming';
    if (location.pathname.includes('/overdue')) return 'overdue';
    return 'today';
  }, [location.pathname]);

  const resolvedBasePath = useMemo(() => {
    if (roleBasePath) return roleBasePath;
    const [, firstSegment] = location.pathname.split('/');
    return firstSegment ? `/${firstSegment}` : '';
  }, [location.pathname, roleBasePath]);

  const fetchRows = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const resp = await getFollowUps(token, {
        bucket,
        q: searchTerm.trim() || undefined,
        status: statusFilter !== 'All' ? statusFilter.toLowerCase() : undefined,
        priority: priorityFilter !== 'All' ? priorityFilter : undefined,
        followUpType: typeFilter !== 'All' ? typeFilter : undefined,
        page,
        pageSize: 10,
      });
      const items = resp?.data?.items || [];
      const meta = resp?.data?.pagination || {};
      setRows(items);
      setPagination({
        page: Number(meta.page || page),
        pageSize: Number(meta.page_size || 10),
        total: Number(meta.total || items.length || 0),
        totalPages: Number(meta.total_pages || 1),
      });
    } catch (err) {
      setRows([]);
      setPagination({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
      });
      toast.error(err?.message || 'Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  }, [bucket, page, priorityFilter, searchTerm, statusFilter, token, typeFilter]);

  useEffect(() => {
    setPage(1);
  }, [bucket, searchTerm, statusFilter, priorityFilter, typeFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const titleMap = {
    today: "Today's Follow-Ups",
    upcoming: 'Upcoming Follow-Ups',
    overdue: 'Overdue Follow-Ups',
    completed: 'Completed Follow-Ups',
  };

  const counts = useMemo(() => ({
    total: rows.length,
    overdue: rows.filter((item) => item.status === 'Overdue').length,
    actionable: rows.filter((item) => item.status !== 'Completed').length,
    completed: rows.filter((item) => item.status === 'Completed').length,
  }), [rows]);

  const showCompletedBucket = bucket === 'completed';

  return (
    <div className="space-y-6">
      <FollowUpEditDialog item={editingItem} open={Boolean(editingItem)} onClose={() => setEditingItem(null)} onSaved={fetchRows} />
      <FollowUpCompleteDialog item={completingItem} open={Boolean(completingItem)} onClose={() => setCompletingItem(null)} onSaved={fetchRows} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">{titleMap[bucket]}</h1>
          <p className="mt-1 text-muted-foreground">Real scheduled follow-ups for this role scope</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {bucketTabs.map((tab) => (
            <Button
              key={tab.key}
              asChild
              size="sm"
              variant={bucket === tab.key ? 'default' : 'outline'}
              className={bucket === tab.key ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              <Link to={`${resolvedBasePath}/followups/${tab.key}`}>{tab.label}</Link>
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="border border-border">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">In This View</p>
                <p className="text-2xl font-bold text-blue-600">{showCompletedBucket ? counts.completed : counts.total}</p>
              </div>
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{showCompletedBucket ? 'Completed' : 'Actionable'}</p>
                <p className="text-2xl font-bold text-amber-600">{showCompletedBucket ? counts.completed : counts.actionable}</p>
              </div>
              <CalendarDays className="h-5 w-5 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{showCompletedBucket ? 'Historic Rows' : 'Overdue'}</p>
                <p className="text-2xl font-bold text-rose-600">{showCompletedBucket ? counts.total : counts.overdue}</p>
              </div>
              <RefreshCw className="h-5 w-5 text-rose-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Search and filter follow-ups in this bucket.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <div className="space-y-1.5 xl:col-span-2">
            <Label>Search</Label>
            <Input
              placeholder="Lead, phone, owner, note, type"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>

          <div className="flex items-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('All');
                setPriorityFilter('All');
                setTypeFilter('All');
              }}
            >
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Follow-Up Schedule</CardTitle>
          <CardDescription>
            {showCompletedBucket
              ? 'Completed follow-up history with completion timestamps and owner details'
              : 'Date, time, type, notes, owner and live actions'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Loading follow-ups...</div>
          ) : (
            <FollowUpTable
              rows={rows}
              onViewLead={(item) => navigate(`${resolvedBasePath}/leads/${item.lead_id || item.leadId}`)}
              onEdit={setEditingItem}
              onComplete={setCompletingItem}
              showCompletedAt={showCompletedBucket}
              readOnly={showCompletedBucket}
            />
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Showing page {pagination.page} of {pagination.totalPages} · {pagination.total} follow-ups
        </p>
        <FollowUpPagination page={page} totalPages={pagination.totalPages} onPageChange={setPage} />
      </div>
    </div>
  );
}

export function FollowUpLeadsPage({ roleBasePath }) {
  const { token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [completingItem, setCompletingItem] = useState(null);

  const fetchRows = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const resp = await getFollowUps(token, { bucket: 'all' });
      const items = resp?.data?.items || [];
      setRows(items);
    } catch (err) {
      setRows([]);
      toast.error(err?.message || 'Failed to load follow-up leads');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const resolvedBasePath = useMemo(() => {
    if (roleBasePath) return roleBasePath;
    const [, firstSegment] = location.pathname.split('/');
    return firstSegment ? `/${firstSegment}` : '';
  }, [location.pathname, roleBasePath]);

  return (
    <div className="space-y-6">
      <FollowUpEditDialog item={editingItem} open={Boolean(editingItem)} onClose={() => setEditingItem(null)} onSaved={fetchRows} />
      <FollowUpCompleteDialog item={completingItem} open={Boolean(completingItem)} onClose={() => setCompletingItem(null)} onSaved={fetchRows} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Follow-Up Leads</h1>
          <p className="mt-1 text-muted-foreground">All follow-up records available in the current role scope</p>
        </div>
        <Button asChild variant="outline">
          <Link to={`${resolvedBasePath}/followups/today`}>
            <MessageSquare className="mr-2 h-4 w-4" />
            Open Follow-Up Buckets
          </Link>
        </Button>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>All Follow-Ups</CardTitle>
          <CardDescription>Pending and completed follow-ups, sorted by schedule</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Loading follow-up leads...</div>
          ) : (
            <FollowUpTable
              rows={rows}
              onViewLead={(item) => navigate(`${resolvedBasePath}/leads/${item.lead_id || item.leadId}`)}
              onEdit={setEditingItem}
              onComplete={setCompletingItem}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
